import { z } from 'zod';

/**
 * Zod schema for the agent-facing collection-lead override (#728, Slice B —
 * `CollectionLeadInput`). Built per-render from the product's ADVISORY
 * client-side bounds (`Product.collectionLeadBounds`,
 * `www/src/features/products/services/productTransform.ts`), since the bound
 * is a runtime value, not a static one — the same posture
 * `buildDepositAmountSchema` (`DepositInputSchema.ts`) already takes for the
 * deposit field.
 *
 * `collectionLeadMonths` is `undefined` when the field is empty — that is a
 * valid state (no override; the product's own configured lead applies, D-B7
 * of the plan), not a validation error. Contract rules:
 *   - a supplied value must be a whole number of months (checked BEFORE the
 *     bounds check, with an early `return`, so a decimal is never ALSO
 *     reported as out of bounds — mirrors `DepositInputSchema.ts:75-81`'s
 *     non-penny-amount check)
 *   - a supplied value must be within `[min, max]` INCLUSIVE, naming
 *     whichever bound it violates
 *
 * NO SEPARATE NEGATIVE CHECK: `min >= 0` is guaranteed by
 * `parseCollectionLeadBounds` (productTransform.ts) before this schema is
 * ever built, so `months < min` already subsumes a negative entry. Stated so
 * a reviewer does not read the absence as an omission.
 *
 * NO `productDefault` PARAMETER: the default is not a validation input — it
 * belongs in `CollectionLeadInput`'s hint only, so this schema stays a pure
 * function of its bounds, exactly like `resolveServiceCountMode.ts`'s
 * coherence check treats `default` as informational, not a bound.
 */
export const buildCollectionLeadSchema = (min: number, max: number) =>
    z.object({
        collectionLeadMonths: z
            .number({ invalid_type_error: 'Enter a whole number of months' })
            .optional()
            .superRefine((months, ctx) => {
                if (months === undefined) {
                    return; // blank = no override = the product's configured lead (D-B7)
                }

                if (!Number.isInteger(months)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'The collection lead must be a whole number of months',
                    });
                    return; // never also report a bounds violation for a non-integer
                }

                if (months < min) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `The collection lead must be at least ${min} month(s)`,
                    });
                }

                if (months > max) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `The collection lead must be at most ${max} month(s)`,
                    });
                }
            }),
    });

export type CollectionLeadFormData = z.infer<ReturnType<typeof buildCollectionLeadSchema>>;
